import {
  AHIcon,
  AHIconType,
  AHText,
  Box,
  Circle,
  useBoxStyle,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, { PropsWithChildren, ComponentProps } from "react";
import { ActivityIndicator, Platform, Pressable, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useNavigationHeaderHeight } from "../../TopLevelNavigators/AHNavigationHeaderContainer";
import { FlatList } from "react-native-gesture-handler";
import Animated, {
  Easing,
  EntryAnimationsValues,
  FadeIn,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import Color from "color";

type PopoverProps = PropsWithChildren<{
  onClose(): void;
  top?: number;
  verticalAlignment?: "top" | "bottom";
  bottom?: number;
  customWidth?: number;
  side?: "left" | "right";
}>;

export function PopoverSectionHeader(props: {
  title: string;
  tintColor?: string;
}) {
  const theme = useTheme();
  return (
    <AHText
      type="subtitle"
      spaceHorizontal="small"
      spaceTop="small"
      spaceBottom="small"
      selectable={false}
      style={{
        color: props.tintColor ?? theme["text-secondary"],
      }}
    >
      {props.title}
    </AHText>
  );
}

export function PopoverListItemTitle(props: { children: React.ReactNode }) {
  return (
    <AHText
      type="list-item-title"
      style={{ flex: 1 }}
      selectable={false}
      numberOfLines={2}
    >
      {props.children}
    </AHText>
  );
}

export function PopoverListItem(
  props: PropsWithChildren<{
    onPress(): void;
    detail?: "chevron" | "none" | "selected-check";
    selected?: boolean;
    selectedColor?: string;
    leftIcon?: AHIconType | "loading";
    left?: React.ReactNode;
  }>
) {
  const detail = props.detail ?? "chevron";
  const theme = useTheme();
  const layout = useLayoutContext();
  const style = useBoxStyle({
    spaceHorizontal: "medium",
    spaceVertical: "medium",
    cornerRadius: "medium",
  });
  return (
    <Pressable
      onPress={props.onPress}
      style={({ pressed }) => [
        style,
        {
          flexDirection: "row",
          alignItems: "center",
          backgroundColor: props.selected
            ? Color(props.selectedColor ?? theme.primary)
                .mix(Color(theme.surface), 0.95)
                .toString()
            : pressed
              ? Color(theme.primary).mix(Color(theme.surface), 0.9).toString()
              : undefined,
        },
      ]}
    >
      {props.left ? (
        <Box spaceRight="small">{props.left}</Box>
      ) : props.leftIcon ? (
        <Circle
          size="smallMedium"
          style={{
            marginRight: layout.space.small,
          }}
        >
          {props.leftIcon === "loading" ? (
            <ActivityIndicator size="small" color="text-secondary" />
          ) : (
            <AHIcon type={props.leftIcon} color="text-secondary" />
          )}
        </Circle>
      ) : null}

      {props.children}
      {detail === "chevron" ? (
        <AHIcon type="chevron-right" spaceLeft="small" color="text-washed" />
      ) : null}
      {detail === "selected-check" ? (
        <AHIcon type="check" spaceLeft="small" color="text-washed" />
      ) : null}
    </Pressable>
  );
}

type FlatListProps<T> = ComponentProps<typeof FlatList<T>>;

export function PopoverList<T>(props: FlatListProps<T>) {
  const layout = useLayoutContext();
  const contentContainerStyle = useBoxStyle({
    spaceVertical: "small",
    spaceHorizontal: "small",
  });
  return (
    <FlatList
      contentContainerStyle={contentContainerStyle}
      initialNumToRender={20}
      scrollIndicatorInsets={{
        top: 0,
        bottom: layout.space.medium,
      }}
      {...props}
    />
  );
}

export function Popover(props: PopoverProps) {
  const side = props.side ?? "right";
  const layout = useLayoutContext();
  const theme = useTheme();
  const safeAreaInsets = useSafeAreaInsets();
  const navigationHeaderHeight = useNavigationHeaderHeight();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  return (
    <View
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        zIndex: 1000,
        width: "100%",
        height: "100%",
        justifyContent:
          props.verticalAlignment === "bottom" ? "flex-end" : "flex-start",
        alignItems: side === "right" ? "flex-end" : "flex-start",
      }}
    >
      <Animated.View
        entering={FadeIn.duration(100).easing(Easing.in(Easing.linear))}
        style={[
          {
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            position: "absolute",
            backgroundColor: isCompact ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.05)",
            zIndex: -1,
          },
        ]}
      >
        <Pressable
          style={{
            flex: 1,
          }}
          onPress={props.onClose}
        />
      </Animated.View>

      <Animated.View
        entering={Platform.OS === "web" ? undefined : CardEnteringAnimation}
        style={{
          width: "100%",
          maxHeight: "90%",
          maxWidth:
            props.customWidth ??
            (Platform.OS === "web" && !isCompact ? 300 : 290),
          marginTop: props.top ?? safeAreaInsets.top + navigationHeaderHeight,
          marginRight:
            side === "right"
              ? safeAreaInsets.right + layout.space.small
              : undefined,
          marginLeft:
            side === "left"
              ? safeAreaInsets.left + layout.space.small
              : undefined,
          marginBottom:
            props.bottom ?? safeAreaInsets.bottom + layout.space.medium,
          borderRadius: layout.radius.large,
          backgroundColor: theme["surface-primary"],
          overflow: "hidden",
        }}
      >
        {props.children}
      </Animated.View>
    </View>
  );
}

export function CardEnteringAnimation(values: EntryAnimationsValues) {
  "worklet";
  const initialValues = {
    originY: values.targetOriginY - 50,

    transform: [{ scale: 0 }],
    opacity: 0,
  };
  const animations = {
    originY: withSpring(values.targetOriginY, {
      mass: 1,
      damping: 36,
      stiffness: 288,
      overshootClamping: false,
    }),

    transform: [
      {
        scale: withTiming(1, {
          duration: 200,
          easing: Easing.inOut(Easing.ease),
        }),
      },
    ],
    opacity: withTiming(1, {
      duration: 200,
      easing: Easing.in(Easing.circle),
    }),
  };
  return {
    initialValues,
    animations,
  };
}
